import { FlexBox } from '@dixa/lib-shared-habla';
import { ConversationRatingEligibilityEvaluationResults } from 'lib-shared-types-api';

import { getContactPointLabel } from '../constants/contactPointKinds';

import {
  getChannelStatusText,
  getFilterFailureReason,
} from './humanReadableTexts';
import {
  FailedFilterItem,
  MatchedButNotSelectedFilterItem,
  SelectedFilterItem,
} from './listItems';

type FilterEvaluationListProps = {
  evaluationData: ConversationRatingEligibilityEvaluationResults;
};

/**
 * Displays filter evaluations as a simple text list instead of a table. More
 * user-friendly and less intimidating than table format.
 */
export const FilterEvaluationList = ({
  evaluationData,
}: FilterEvaluationListProps): JSX.Element => {
  const selectedFilterInfo = evaluationData.highestPrioEligibleForInfo;

  return (
    <FlexBox flexDirection="column" gap="200">
      {evaluationData.filterEvaluations.map((filterEval) => {
        // Check if this filter is completely ineligible
        const filterIsIneligible = !filterEval.eligibleIndividual;
        const filterHasNoChannels =
          filterEval.outgoingChannelEvaluations.length === 0;
        const shouldShowAsFailedFilter =
          filterIsIneligible || filterHasNoChannels;

        // Scenario 1: Filter failed - show why it failed
        if (shouldShowAsFailedFilter) {
          const reasonText = `NOT triggered: ${getFilterFailureReason(filterEval.kind)}`;

          return (
            <FailedFilterItem
              key={filterEval.filter.id}
              filter={filterEval.filter}
              reasonText={reasonText}
            />
          );
        }

        // Scenario 2: Filter passed - show all channel delivery options
        return filterEval.outgoingChannelEvaluations.map((channelEval) => {
          // Determine if this channel has a valid survey configured
          const surveyExists = channelEval.kind !== 'SurveyDoesNotExist';
          const surveyInfo = surveyExists ? channelEval.survey : null;

          // Check if this filter passed all conditions (not just individual checks)
          const filterMatchedAllConditions =
            filterEval.kind === 'MatchOnConditions';

          const filterPassedCompletePath = filterEval.eligibleRecursive;

          const filterIsFullyEligible =
            filterMatchedAllConditions && filterPassedCompletePath;

          // Check if this specific filter+channel combination was selected to send
          // Note: We must check both channelReference AND contactPointValue because
          // the same survey can be delivered via multiple contact point types
          // (e.g., same survey ID for both email and phone delivery)
          const matchesSelectedFilter =
            selectedFilterInfo?.filter.id === filterEval.filter.id;

          const matchesSelectedChannel =
            selectedFilterInfo?.channelReference ===
            channelEval.deliveryVia.channelReference;

          const matchesSelectedContactPoint =
            selectedFilterInfo?.contactPointValueTarget ===
            channelEval.deliveryVia.contactPointValue;

          const isFilterTheSelectedOption =
            filterIsFullyEligible &&
            matchesSelectedFilter &&
            matchesSelectedChannel &&
            matchesSelectedContactPoint;

          // Determine if this channel option is technically viable
          const channelIsEligible =
            channelEval.kind === 'EligibleOutgoingChannel';

          const contactPointLabel = getContactPointLabel(
            channelEval.deliveryVia.contactPointKind,
          );

          // Language evaluations are only available for channels with existing surveys
          const languageEvaluations = surveyExists
            ? channelEval.languageEvaluations
            : [];

          // Generate unique key for this channel option
          // Include contact point value to handle cases where same channel delivers
          // to multiple contact point types (e.g., email and phone)
          const itemKey = `${filterEval.filter.id}-${channelEval.deliveryVia.channelReference}-${channelEval.deliveryVia.contactPointKind}-${channelEval.deliveryVia.contactPointValue}`;

          // Render the selected option with special highlighting
          if (isFilterTheSelectedOption && surveyInfo) {
            return (
              <SelectedFilterItem
                key={itemKey}
                filter={filterEval.filter}
                survey={surveyInfo}
                contactPointLabel={contactPointLabel}
                contactPointValue={channelEval.deliveryVia.contactPointValue}
                languageEvaluations={languageEvaluations}
              />
            );
          }

          // For non-selected options, determine the appropriate status message
          const statusText = getChannelStatusText(
            channelEval.kind,
            channelIsEligible,
            filterIsFullyEligible,
          );

          // Render as matched-but-not-selected or failed filter
          return (
            <MatchedButNotSelectedFilterItem
              key={itemKey}
              filter={filterEval.filter}
              survey={surveyInfo}
              contactPointLabel={contactPointLabel}
              contactPointValue={channelEval.deliveryVia.contactPointValue}
              statusText={statusText}
              languageEvaluations={languageEvaluations}
              isSuccess={channelIsEligible}
              isEligibleRecursive={filterIsFullyEligible}
            />
          );
        });
      })}
    </FlexBox>
  );
};
